import { createPageAction, noticeError } from 'utils/errorLogger/newRelic';
import { getHostname } from 'utils/location/hostname';
import { isStaging } from 'utils/location/isStaging';
import { getDomain } from 'utils/location/domain';
import Cookies from 'js-cookie';
import { hasConsentForCookie } from './cookieConsent';

export enum ENV {
  STG = 'STG',
  PRD = 'PRD',
}

export interface CookieAttributes {
  expires?: number | Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

let STORAGE_INITIALIZED = false;

const logAndThrow = (errorMsg: string, customAttrs = {}) => {
  const error = new Error(errorMsg);

  noticeError(error, customAttrs);
  throw error;
};

function triggerNotInitializedError(cookieName: string, method: string): undefined {
  logAndThrow(
    // eslint-disable-next-line max-len
    'You are trying to access cookie storage before initialisation. initializeCookieStorage method has to be called prior to any cookie storage access.',
    { cookieName, method }
  );
  return undefined;
}

/**@internal */
export function handleExpectedErrors(error: any) {
  const skipUnwantedError = error.name === 'ForbiddenAccessError';
  if (skipUnwantedError) return;

  createPageAction(error.name, {
    source: 'eprivacy-react-sdk',
    capability: 'cookies_compliance',
    ...error,
  });
}

export interface FixlyCookieStorage {
  /**
   * (Browser only) Returns cookie value or the default if the key was not found
   *
   * @param key Cookie name (key)
   * @param defaultValue Default value in case the cookie is not present or
   * the user did not give their consent to store the cookie
   * @returns Cookie value
   */
  get: (key: string, defaultValue?: string) => string | undefined;

  /**
   * (Browser only) sets cookie in users browser
   *
   * @param key string
   * @param value string
   * @param extraParams {}
   * @param setDomain boolean
   * @returns undefined
   */
  set: <Value extends { toString(): string }>(
    key: string,
    value: Value,
    extraParams?: CookieAttributes,
    setDomain?: boolean
  ) => string | undefined;

  /**
   * (Browser only) Removes cookie from user's browser
   *
   * @param key Cookie name (key)
   * @param params Cookie attributes with which the cookie was previously set
   * @param wasSetDomain If an explicit domain has been specified when setting the cookie
   */
  remove: (key: string, params?: CookieAttributes, wasSetDomain?: boolean) => void;
  isInitialized: boolean;
}

/**
 * Cookie storage interface. initializeCookieStorage has to be called first for this interface to be functional,
 * otherwise it will throw an error when used.
 */

export const cookieStorage: FixlyCookieStorage = {
  get: (key) => triggerNotInitializedError(key, 'get'),
  set: (key) => triggerNotInitializedError(key, 'set'),
  remove: (key) => triggerNotInitializedError(key, 'remove'),
  isInitialized: STORAGE_INITIALIZED,
};

/**
 * Initialize the cookie storage.
 * Sets up get/set/remove functions with consent checking via CookieYes.
 *
 * This function should be called immediately after your application starts and before any access
 * to cookie storage happens.
 *
 * @param env Environment (STG or PRD) - kept for backward compatibility
 * @param debug Enable debug logging
 */
export async function initializeCookieStorage(env: string, debug = false) {
  if (!STORAGE_INITIALIZED) {
    cookieStorage['get'] = createGet();
    cookieStorage['set'] = createSet();
    cookieStorage['remove'] = createRemove();

    STORAGE_INITIALIZED = true;

    if (debug) {
      console.log('[CookieStorage] Initialized with CookieYes consent management');
    }
  }
}

/**
 * Special domains that need custom domain handling for cookie scope.
 *
 * These environments have complex subdomain structures (e.g., AWS staging with
 * multiple subdomain levels) where the standard domain extraction doesn't work correctly.
 *
 * ⚠️ NOTE: Current regex patterns include protocol/path but test against hostname only,
 * so this condition always returns false. This may be intentional (letting cookies use
 * default hostname scope) or a bug that should be fixed.
 *
 * To fix, use: /^fixly\.stg\.01\.eu-west-1\.eu\.olx\.org$/ (no protocol/path)
 */
const listOfDomainsRegex = [
  /^https:\/\/fixly\.stg\.01\.eu-west-1\.eu\.olx\.org\/$/, // AWS staging (complex subdomain)
  /^http:\/\/fixpl-local\.dev\.rebbix\.com:3000/, // Local development
];

/**
 * Checks if current hostname requires special domain handling.
 *
 * For special staging/local environments, we use getDomain(false) which extracts
 * 5 domain components instead of 2, to properly scope cookies to the environment
 * rather than the entire top-level domain.
 *
 * Example:
 * - Normal: getDomain(true) on "fixly.stg.01.eu-west-1.eu.olx.org" → "olx.org" (too broad)
 * - Rewrite: getDomain(false) → "eu-west-1.eu.olx.org" (better scoped to AWS region)
 *
 * @returns true if current domain needs special handling
 */
const needsCookieDomainRewrite = (): boolean => {
  return listOfDomainsRegex.some((regex) => regex.test(getHostname()));
};

const createGet = () => (key: string, defaultValue?: string) => {
  // Check consent before reading cookie
  if (!hasConsentForCookie(key)) {
    console.warn(`[CookieStorage] Cannot get cookie "${key}" - consent not granted`);
    return defaultValue;
  }

  const value = Cookies.get(key);
  return value === undefined ? defaultValue : value;
};

const createSet =
  () =>
  <Value extends { toString(): string }>(key: string, value: Value, extraParams = {}, setDomain = true) => {
    // Check consent before setting cookie
    if (!hasConsentForCookie(key)) {
      console.warn(`[CookieStorage] Cannot set cookie "${key}" - consent not granted`);
      return undefined;
    }

    // Domain handling logic
    let params: CookieAttributes = {
      expires: 999,
      ...extraParams,
    };
    if (setDomain) {
      if (!isStaging()) {
        // Production: Use standard domain extraction (e.g., "fixly.pl")
        params = { domain: getDomain(), ...params };
      } else if (isStaging() && needsCookieDomainRewrite()) {
        // Special staging environments: Use extended domain (5 components instead of 2)
        // to properly scope cookies to the environment without being too broad
        console.log(params);
        params = { domain: getDomain(false), ...params };
      }
      // Other staging: No explicit domain (uses current hostname for isolation)
    }

    return Cookies.set(key, value.toString(), params);
  };

const createRemove =
  () =>
  (key: string, params = {}, wasSetDomain = true) => {
    // No consent check for removal (always allow deletion)

    // Domain handling: Must match the domain used when setting the cookie
    if (wasSetDomain) {
      if (!isStaging()) {
        // Production: Use same domain as set operation
        params = { domain: getDomain(), ...params };
      } else if (isStaging() && needsCookieDomainRewrite()) {
        // Special staging: Use same extended domain as set operation
        params = { domain: getDomain(false), ...params };
      }
      // Other staging: No explicit domain (matches set behavior)
    }

    return Cookies.remove(key, params);
  };
